/**
 * 登录弹窗组件 - 手机号验证码登录
 */
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, MailCheck, Shield } from "lucide-react";
import HeadlessDialog from './Dialog/HeadlessDialog';
import CaptchaDialog from "./CaptchaDialog";
import { userApi } from "@/api/modules/user";

interface LoginDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
}

const LoginDialog = ({ isOpen, onClose, onLoginSuccess }: LoginDialogProps) => {
  const [formData, setFormData] = useState({
    phone: "",
    smsCode: ""
  });
  const [errors, setErrors] = useState<Record<string, string>>({
    phone: "",
    smsCode: ""
  });
  const [apiError, setApiError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsSent, setSmsSent] = useState(false);
  const [isCaptchaDialogOpen, setIsCaptchaDialogOpen] = useState(false);

  // 重置表单
  const resetForm = () => {
    setFormData({ phone: "", smsCode: "" });
    setErrors({ phone: "", smsCode: "" });
    setApiError("");
    setIsLoading(false);
    setSmsCountdown(0);
    setSmsSent(false);
  };

  // 当对话框关闭时重置表单
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
    if (apiError) {
      setApiError("");
    }
  };

  // 计算手机号是否有效
  const isPhoneValid = /^1[3-9]\d{9}$/.test(formData.phone);

  // 打开图形验证码弹窗
  const handleSendSmsCode = () => {
    if (!isPhoneValid) {
      setErrors(prev => ({...prev, phone: "请输入有效的手机号码"}));
      return;
    }
    setIsCaptchaDialogOpen(true);
  };

  // 图形验证成功后发送短信验证码
  const handleCaptchaVerifySuccess = async (captchaUuid: string, captchaCode: string) => {
    try {
      const response = await userApi.sendSmsCode({
        phone: formData.phone,
        uuid: captchaUuid,
        code: captchaCode,
        clientId: "pc"
      });

      if (response?.code === 500) {
        setApiError(`发送失败${response.msg ? ': ' + response.msg : ''}`);
        return;
      }

      // 设置倒计时（60秒）
      setSmsCountdown(60);
      setSmsSent(true);
      setApiError("");

      // 启动倒计时
      const timer = setInterval(() => {
        setSmsCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("发送验证码失败:", error);
      setApiError("发送验证码失败，请稍后重试");
    }
  };

  const handleLogin = async () => {
    // 验证表单
    const newErrors: Record<string, string> = {};
    
    if (!formData.phone.trim()) {
      newErrors.phone = "请输入手机号码";
    } else if (!/^1[3-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = "请输入有效的手机号码";
    }

    if (!formData.smsCode.trim()) {
      newErrors.smsCode = "请输入短信验证码";
    } else if (formData.smsCode.length !== 6) {
      newErrors.smsCode = "请输入6位短信验证码";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setApiError("");

    try {
      // 调用登录接口
      const response = await userApi.codeLogin({
        phone: formData.phone,
        smsCode: formData.smsCode,
      });

      if (response?.code === 500) {
        setApiError(`登录失败${response.msg ? ': ' + response.msg : ''}`);
        setIsLoading(false);
        return;
      }

      if (response?.code === 200) {
        // 保存 token
        const token = response?.data?.token;
        if (token) {
          localStorage.setItem('token', token);
        }
        
        // 登录成功回调
        onLoginSuccess();
        resetForm();
        onClose();
      }
    } catch (error) {
      console.error("登录失败:", error);
      setApiError("登录失败，请稍后重试");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onClose();
    }
  };

  return (
    <>
      <HeadlessDialog
        isOpen={isOpen}
        onClose={handleClose}
        title="登录社区"
        description="使用手机号验证码登录光智云社区"
        size="sm"
        isShowClose={false}
      >
        <div className="space-y-6 py-4">
          {/* 手机号 */}
          <div>
            <Label htmlFor="login-phone" className="flex items-center space-x-2 mb-2">
              <Phone className="w-4 h-4"/>
              <span>手机号码 *</span>
            </Label>
            <div className="flex items-center space-x-2">
              <Input
                id="login-phone"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                placeholder="请输入手机号码"
                className={`flex-1 ${errors.phone ? "border-red-500" : ""}`}
                maxLength={11}
                disabled={isLoading}
              />
              <Button
                type="button"
                onClick={handleSendSmsCode}
                disabled={!isPhoneValid || smsCountdown > 0 || isLoading}
                className="whitespace-nowrap"
              >
                {smsCountdown > 0 ? `${smsCountdown}秒后重发` : "发送验证码"}
              </Button>
            </div>
            {errors.phone && (
              <p className="text-sm text-red-500 mt-1">{errors.phone}</p>
            )}
          </div>

          {/* 短信验证码 */}
          <div>
            <Label htmlFor="login-smsCode" className="flex items-center space-x-2 mb-2">
              <MailCheck className="w-4 h-4"/>
              <span>短信验证码 *</span>
            </Label>
            <Input
              id="login-smsCode"
              value={formData.smsCode}
              onChange={(e) => handleInputChange("smsCode", e.target.value)}
              placeholder="请输入短信验证码"
              className={errors.smsCode ? "border-red-500" : ""}
              maxLength={6}
              disabled={isLoading}
            />
            {errors.smsCode && (
              <p className="text-sm text-red-500 mt-1">{errors.smsCode}</p>
            )}
            {formData.phone && smsSent && (
              <p className="text-sm text-gray-600 mt-2">
                验证码已发送至 {formData.phone}
              </p>
            )}
          </div>

          {/* 错误提示 */}
          {apiError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {apiError}
            </div>
          )}

          {/* 按钮 */}
          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1"
            >
              取消
            </Button>
            <Button
              type="button"
              onClick={handleLogin}
              disabled={isLoading || !formData.phone || !formData.smsCode}
              className="flex-1"
            >
              {isLoading ? "登录中..." : "登录"}
            </Button>
          </div>
        </div>
      </HeadlessDialog>

      {/* 图形验证码弹窗 */}
      <CaptchaDialog
        isOpen={isCaptchaDialogOpen}
        onClose={() => setIsCaptchaDialogOpen(false)}
        onVerifySuccess={handleCaptchaVerifySuccess}
        phone={formData.phone}
      />
    </>
  );
};

export default LoginDialog;
